import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    Snackbar,
} from "@mui/material"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import RefreshIcon from "@mui/icons-material/Refresh"
import { useTranslation } from "react-i18next"
import statsService from "../services/statsService"
import { useCompanyId } from "../hooks/useCompanyId"
import apiClient from "../services/apiClient"

const fmt = (amount: number, currency = "EUR", locale = "fr") => {
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
        }).format((amount || 0) / 100)
    } catch {
        return `${((amount || 0) / 100).toFixed(2)} ${currency}`
    }
}

const StatsPage: React.FC = () => {
    const { t, i18n } = useTranslation()
    const { companyId } = useCompanyId()
    const locale = i18n.language || "fr"

    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [outletId, setOutletId] = useState("")
    const [outlets, setOutlets] = useState<any[]>([])
    const [dateFrom, setDateFrom] = useState("")
    const [dateTo, setDateTo] = useState("")

    useEffect(() => {
        if (!companyId) {
            setOutlets([])
            setOutletId("")
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get(
                    `/api/outlets?company_id=${companyId}`
                )
                if (!cancelled) setOutlets(res.outlets || [])
            } catch (err) {
                console.error("Failed to load outlets:", err)
                if (!cancelled) setOutlets([])
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId])

    const loadStats = useCallback(async () => {
        if (!companyId) return
        setLoading(true)
        try {
            const result = await statsService.getDashboard(companyId, {
                outletId: outletId || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            })
            setData(result)
        } catch (err) {
            console.error("Failed to load stats:", err)
            setError((err as any)?.message || t("statsPage.errors.loadFailed"))
        } finally {
            setLoading(false)
        }
    }, [companyId, outletId, dateFrom, dateTo, t])

    useEffect(() => {
        loadStats()
    }, [loadStats])

    const kpis = data?.kpis
    const currency = kpis?.currency || "EUR"
    const formatDate = useCallback(
        (value: string | Date) =>
            new Intl.DateTimeFormat(locale, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            }).format(new Date(value)),
        [locale]
    )

    const getDocumentTypeLabel = useCallback(
        (type: string) =>
            t(`statsPage.documentTypes.${type}`, {
                defaultValue: type,
            }),
        [t]
    )

    const getDocumentStatusLabel = useCallback(
        (status: string) =>
            t(`statsPage.documentStatuses.${status}`, {
                defaultValue: status,
            }),
        [t]
    )

    return (
        <Container maxWidth="xl">
            <Box sx={{ my: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography variant="h4">{t("statsPage.title")}</Typography>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadStats}
                        disabled={loading}
                    >
                        {t("statsPage.actions.refresh")}
                    </Button>
                </Box>

                {/* Filters */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>
                                    {t("statsPage.filters.outlet")}
                                </InputLabel>
                                <Select
                                    value={outletId}
                                    label={t("statsPage.filters.outlet")}
                                    onChange={(e) =>
                                        setOutletId(e.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        {t("statsPage.filters.allOutlets")}
                                    </MenuItem>
                                    {outlets.map((o) => (
                                        <MenuItem key={o.id} value={o.id}>
                                            {o.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label={t("statsPage.filters.dateFrom")}
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label={t("statsPage.filters.dateTo")}
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {/* KPI Cards */}
                {kpis && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <TrendingUpIcon color="primary" />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t("statsPage.kpis.totalRevenue")}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        fontWeight={700}
                                        color="primary"
                                    >
                                        {fmt(
                                            kpis.totalRevenue,
                                            currency,
                                            locale
                                        )}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <ReceiptLongIcon color="info" />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t("statsPage.kpis.documents")}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h5" fontWeight={700}>
                                        {kpis.documentCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <ShoppingCartIcon color="success" />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t("statsPage.kpis.sales")}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h5" fontWeight={700}>
                                        {kpis.salesCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <AttachMoneyIcon color="warning" />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t("statsPage.kpis.avgTicket")}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h5" fontWeight={700}>
                                        {fmt(
                                            kpis.averageTicket,
                                            currency,
                                            locale
                                        )}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {data && (
                    <Grid container spacing={3}>
                        {/* Revenue Time Series */}
                        {data.revenueTimeSeries &&
                            data.revenueTimeSeries.length > 0 && (
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t(
                                                "statsPage.sections.dailyRevenue"
                                            )}
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            {t(
                                                                "statsPage.table.date"
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {t(
                                                                "statsPage.table.revenue"
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {t(
                                                                "statsPage.table.sales"
                                                            )}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                width: "40%",
                                                            }}
                                                        >
                                                            {t(
                                                                "statsPage.table.distribution"
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {data.revenueTimeSeries.map(
                                                        (row: any) => {
                                                            const maxRev =
                                                                Math.max(
                                                                    ...data.revenueTimeSeries.map(
                                                                        (
                                                                            r: any
                                                                        ) =>
                                                                            r.revenue
                                                                    )
                                                                )
                                                            const pct =
                                                                maxRev > 0
                                                                    ? (row.revenue /
                                                                          maxRev) *
                                                                      100
                                                                    : 0
                                                            return (
                                                                <TableRow
                                                                    key={
                                                                        row.date
                                                                    }
                                                                >
                                                                    <TableCell>
                                                                        {formatDate(
                                                                            row.date
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {fmt(
                                                                            row.revenue,
                                                                            currency,
                                                                            locale
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {
                                                                            row.count
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <LinearProgress
                                                                            variant="determinate"
                                                                            value={
                                                                                pct
                                                                            }
                                                                            sx={{
                                                                                height: 8,
                                                                                borderRadius: 4,
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        }
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Grid>
                            )}

                        {/* Revenue by Category */}
                        {data.revenueByCategory &&
                            data.revenueByCategory.length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t(
                                                "statsPage.sections.revenueByCategory"
                                            )}
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            {t(
                                                                "statsPage.table.category"
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {t(
                                                                "statsPage.table.revenue"
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {t(
                                                                "statsPage.table.percentage"
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {data.revenueByCategory.map(
                                                        (row: any) => (
                                                            <TableRow
                                                                key={
                                                                    row.categoryId
                                                                }
                                                            >
                                                                <TableCell>
                                                                    {
                                                                        row.categoryName
                                                                    }
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {fmt(
                                                                        row.revenue,
                                                                        currency,
                                                                        locale
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {
                                                                        row.percentage
                                                                    }
                                                                    %
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Grid>
                            )}

                        {/* Revenue by Payment Method */}
                        {data.revenueByPaymentMethod &&
                            data.revenueByPaymentMethod.length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t(
                                                "statsPage.sections.revenueByPaymentMethod"
                                            )}
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            {t(
                                                                "statsPage.table.method"
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {t(
                                                                "statsPage.table.revenue"
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {t(
                                                                "statsPage.table.percentage"
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {data.revenueByPaymentMethod.map(
                                                        (row: any) => (
                                                            <TableRow
                                                                key={
                                                                    row.methodId
                                                                }
                                                            >
                                                                <TableCell>
                                                                    {
                                                                        row.methodName
                                                                    }
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {fmt(
                                                                        row.revenue,
                                                                        currency,
                                                                        locale
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {
                                                                        row.percentage
                                                                    }
                                                                    %
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Grid>
                            )}

                        {/* Documents by Type */}
                        {data.documentsByType &&
                            Object.keys(data.documentsByType).length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t(
                                                "statsPage.sections.documentsByType"
                                            )}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {Object.entries(
                                                data.documentsByType
                                            ).map(
                                                ([type, count]: [
                                                    string,
                                                    any
                                                ]) => (
                                                    <Chip
                                                        key={type}
                                                        label={t(
                                                            "statsPage.chips.valueWithCount",
                                                            {
                                                                label: getDocumentTypeLabel(
                                                                    type
                                                                ),
                                                                count,
                                                            }
                                                        )}
                                                        variant="outlined"
                                                        color="primary"
                                                    />
                                                )
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}

                        {/* Documents by Status */}
                        {data.documentsByStatus &&
                            Object.keys(data.documentsByStatus).length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t(
                                                "statsPage.sections.documentsByStatus"
                                            )}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {Object.entries(
                                                data.documentsByStatus
                                            ).map(
                                                ([status, count]: [
                                                    string,
                                                    any
                                                ]) => (
                                                    <Chip
                                                        key={status}
                                                        label={t(
                                                            "statsPage.chips.valueWithCount",
                                                            {
                                                                label: getDocumentStatusLabel(
                                                                    status
                                                                ),
                                                                count,
                                                            }
                                                        )}
                                                        variant="outlined"
                                                        color={
                                                            status ===
                                                            "validated"
                                                                ? "success"
                                                                : status ===
                                                                  "cancelled"
                                                                ? "error"
                                                                : "default"
                                                        }
                                                    />
                                                )
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}
                    </Grid>
                )}

                {!data && !loading && (
                    <Paper sx={{ p: 4, textAlign: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                            {t("statsPage.empty.noData")}
                        </Typography>
                    </Paper>
                )}

                <Snackbar
                    open={!!error}
                    autoHideDuration={5000}
                    onClose={() => setError(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() => setError(null)}
                        severity="error"
                        variant="filled"
                    >
                        {error}
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    )
}

export default StatsPage
